<template>
  <div>
    <v-overlay 
        v-if="isLoading"
        :value="isLoading"
    >  
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
        style="position: fixed;"
      ></v-progress-circular>
    </v-overlay>
    <v-dialog
        v-else
        v-model="isShow"
        v-bind="attrs"
        v-on="listeners"
        persistent
        ref="dialog"
        :content-class="draggable && 'dialog-draggable' || ''"
        @click:outside="onClickOutside"
    >
        <v-card
            prepend-icon="mdi-file-document-plus-outline"
            :title="title"
            :loading="isLoading"
            ref="dialogContent"
            :style="dialogCss"
        >
            <v-card-title
                class="draggable-header"
                @mousedown="startDrag"
            ><span class="dialog-title">{{title}}</span><v-icon @click="$emit('close')" :color="titleColor" right style="margin-left: auto;">mdi-close</v-icon></v-card-title>
            <slot name="beforeText" />
            <v-card-text>
                <v-form-modified-stored
                    v-model="isValid"
                    :data.sync="formData"
                    :was-modified.sync="wasModified"
                    :disabled="isLoading"
                    :loading="isLoading"
                    ref="form"
                >
                    <template v-slot:default>
                        <slot name="activator" />
                        <slot />
                    </template>
                </v-form-modified-stored>
            </v-card-text>
            <v-card-actions>
              <slot name="actions">
                <v-spacer></v-spacer>
                <v-btn
                    :disabled="isLoading"
                    @click="$emit('close')"
                >{{ cancelTitle }}</v-btn>
                <v-btn
                    :disabled="!isCanSubmit"
                    :loading="isLoading"
                    color="primary"
                    @click="$emit('ok')"
                >{{ okTitle }}</v-btn>
              </slot>
            </v-card-actions>
        </v-card>
    </v-dialog>
  </div>
</template>
  
<script>
import CommonRules from "@/mixins/CommonRules";
import UserAuth from "@/mixins/UserAuth";
  export default {
    name: "VDialogFormDraggable",
    components: {
        VFormModifiedStored: () => import('@/components/commonComponents/VFormModifiedStored'),
    },
    mixins: [
        CommonRules,
        UserAuth,
    ],
    props: {
      value: Boolean,
      draggable: {
        type: Boolean,
        default: true,
      },
      title: String,
      disabled: Boolean,
      loading: Boolean,
      saving: Boolean,
      formData: {
        type: Object,
        default: () => ({}),
      },
      titleBackground: {
        type: String,
        default: '#fff',
      },
      titleColor: {
        type: String,
        default: '#000',
      },
      okTitle: {
        type: String,
        default: 'Ok',
      },
      cancelTitle: {
        type: String,
        default: 'Отмена',
      },
    },
    data() {
      return {
        dialogPosition: { top: 100, left: 100 },
        isDragging: false,

        dragOffset: { x: 0, y: 0 },
        isValid: false,
        wasModified: false,
      };
    },
    computed: {
        attrs() {
            return {...this.$attrs, scrollable: this.$attrs.scrollable && !this.isLoading};
        },
        listeners() {
            return this._.omit(this.$listeners, []);
        },
        dialogCss() {
          return {
              '--title-background': this.titleBackground,
              '--title-color': this.titleColor,
          };
        },
        isDisabled() {
            return this.disabled;
        },
        isCanSubmit() {
            return !this.isDisabled && this.isValid && this.wasModified && !this.isLoading;
        },
        isLoading() {
            return this.loading || this.saving;
        },
        isShow: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit('input', v);
            }
        },
    },
    watch: {
      isValid(v) {
        this.$emit('validation', v);
      },
      isShow(v) {
        this.$nextTick(() => {
          if (v && this.draggable) {
            this.setToDialogStyle({left: 'none', top: 'none', position: 'absolute'});
          } else {
            this.setToDialogStyle({position: 'relative'});
          }
        });
      },
      wasModified(v) {
        this.$emit('wasModified', v);
      },
    },
    mounted() {
      window.addEventListener("keydown", this.handleEscape);
    },
    beforeDestroy() {
      window.removeEventListener("keydown", this.handleEscape);
    },
    methods: {
      setToDialogStyle(styles) {
        const dialog = this.$refs.dialog?.$refs?.dialog;
        if (!dialog?.style || !styles) {
          return;
        }
        this._.forOwn(styles, (v, k) => {
          dialog.style[k] = v;
        });
      },
      startDrag(event) {
        if (!this.draggable) return;
        this.isDragging = true;
        const dialogContent = this.$refs.dialogContent.$el;
        const dialogRect = dialogContent.getBoundingClientRect();
        this.dragOffset = {
          x: event.clientX - (dialogRect.left - 24),
          y: event.clientY - (dialogRect.top - 24),
        };
        document.addEventListener("mousemove", this.onDrag);
        document.addEventListener("mouseup", this.stopDrag);
      },
      onDrag(event) {
        if (!this.isDragging) return;
    
        const viewportWidth = window.innerWidth,
              viewportHeight = window.innerHeight,
              dialogContent = this.$refs.dialogContent.$el,
              dialogRect = dialogContent.getBoundingClientRect(),
              minLeft = -dialogRect.width + 60 ,
              maxLeft = viewportWidth - 60,
              maxTop = viewportHeight - 60, // 
              newLeft = Math.max(minLeft - 24, Math.min(event.clientX - this.dragOffset.x, maxLeft - 24)),
              newTop = Math.max(-24, Math.min(event.clientY - this.dragOffset.y, maxTop));

          this.dialogPosition = {
              top: newTop,
              left: newLeft,
          };

          requestAnimationFrame(() => {
              this.setToDialogStyle({left: `${this.dialogPosition.left}px`, top: `${this.dialogPosition.top}px`});
          });
        
      },
      stopDrag() {
        this.isDragging = false;
        document.removeEventListener("mousemove", this.onDrag);
        document.removeEventListener("mouseup", this.stopDrag);
      },
      onClickOutside(e) {
        if (this.wasModified) {
          return;
        }
        const exceptionElements = document.getElementsByClassName("vue-notification-group");
        // Если клик был по исключенному элементу - НЕ закрываем диалог
        if (exceptionElements?.length && exceptionElements[0].contains(e.target)) {
          return;
        }
        // Закрываем диалог только если клик был вне него и НЕ на исключении
        this.isShow = false;
        
      },
      handleEscape(event) {
        if (event.key !== "Escape" || this.wasModified) {
          return;
        }
        this.isShow = false;
      },
    },
  };
</script>
<style scoped lang="scss">
    .v-dialog > .v-card {
        & >.v-card__text {
            //padding-top: 15px;
        }
        & > .v-card__actions {
            padding: 0 24px 20px;
        }
        & > .v-card__title {
            user-select: none;
            background-color: var(--title-background)!important;
            border-color: var(--title-background)!important;
            color: var(--title-color);
        }
    }
    .v-dialog.dialog-draggable > .v-card {
        & > .v-card__title {
            cursor: move; /* fallback if grab cursor is unsupported */
            cursor: grab;
            cursor: -moz-grab;
            cursor: -webkit-grab;
        }
    }
    .dialog-title {
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1 1 calc(100% - 60px);
      white-space: nowrap;
    }
</style>
